import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ArrowIcon, PhoneLink, SERVICES, SITE_URL, Seo, SiteFooter, SiteHeader, faqJsonLd, placeJsonLd, providerJsonLd,
} from '../components/shared';
import {
  BeforeAfterGallery, Faq, HowItWorks, ReviewPrompt, TrustBar,
} from '../components/OwnerSections';
import {
  CITIES, LOCAL_SERVICE_ORDER, SERVICE_INFO, cityPath, cityServicePath, findCity,
} from '../data/locations';

// /home-owners/:city and /home-owners/:city/:service. All copy comes from
// data/locations.js; a city or service without an entry there goes to /error.

const HUB_HERO = { src: '/photos/front-lawn-curb.jpg', w: 1000, h: 844, alt: 'A mowed front lawn with yard waste bagged at the curb' };

function serviceByUrlSlug(urlSlug) {
  return SERVICES.find((s) => s.urlSlug === urlSlug);
}

function bookPath(urlSlug) {
  return urlSlug ? `/book?service=${urlSlug}` : '/book';
}

// City-page services first (in LOCAL_SERVICE_ORDER), then the rest.
function cityServices(city) {
  const ranked = (s) => {
    const i = LOCAL_SERVICE_ORDER.indexOf(s.urlSlug);
    return i === -1 ? LOCAL_SERVICE_ORDER.length : i;
  };
  return [...SERVICES]
    .sort((a, b) => ranked(a) - ranked(b))
    .map((s) => ({ ...s, page: city.pages[s.urlSlug] ? cityServicePath(city, s.urlSlug) : null }));
}

function listNames(names) {
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
}

function breadcrumbJsonLd(crumbs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.label,
      item: `${SITE_URL}${c.to}`,
    })),
  };
}

function Crumbs({ crumbs }) {
  return (
    <nav className="crumbs" aria-label="Breadcrumb">
      <ol>
        {crumbs.map((c, i) => (
          <li key={c.to}>
            {i === crumbs.length - 1 ? <span aria-current="page">{c.label}</span> : <Link to={c.to}>{c.label}</Link>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function LocationHeader({ quoteTo }) {
  return (
    <SiteHeader>
      <Link to="/home-owners" className="nav-link nav-link--wide">Home owners</Link>
      <Link to="/students" className="nav-link nav-link--wide">For students</Link>
      <PhoneLink />
      <Link to={quoteTo} className="btn btn--primary btn--header">Get a quote</Link>
    </SiteHeader>
  );
}

function LocationHero({ image, crumbs, title, intro, quoteTo }) {
  return (
    <section className="hero hero--owners hero--local">
      <img className="hero__img" src={image.src} alt={image.alt} width={image.w} height={image.h} loading="eager" fetchPriority="high" />
      <div className="hero__scrim" />
      <div className="hero__inner">
        <div className="wrap">
          <div className="hero__copy">
            <Crumbs crumbs={crumbs} />
            <h1 className="h1">{title}</h1>
            <p className="hero__lede">{intro}</p>
            <div className="hero__actions">
              <Link to={quoteTo} className="btn btn--primary btn--lg">Get a free quote <ArrowIcon /></Link>
              <a href="#how" className="btn btn--ghost">How it works</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Neighbourhoods({ city }) {
  return (
    <ul className="chip-list" aria-label={`Neighbourhoods in ${city.name}`}>
      {city.neighbourhoods.map((n) => <li key={n}>{n}</li>)}
    </ul>
  );
}

function QuoteBand({ title, quoteTo }) {
  return (
    <section className="section section--navy section--center">
      <div className="wrap">
        <h2 className="h2">{title}</h2>
        <p>Send us the job and we will come back with a price. No hourly rates, no subscription, no obligation.</p>
        <Link to={quoteTo} className="btn btn--primary btn--lg btn--on-navy">Get a free quote <ArrowIcon /></Link>
      </div>
    </section>
  );
}

export function CityHubPage() {
  const { city: citySlug } = useParams();
  const city = findCity(citySlug);
  if (!city) return <Navigate to="/error" replace />;

  const crumbs = [
    { to: '/home-owners', label: 'Home owners' },
    { to: cityPath(city), label: city.name },
  ];
  const services = cityServices(city);
  const nearby = city.nearby.map(findCity);

  return (
    <div className="page">
      <Seo
        title={`Yard Work & Snow Removal in ${city.name} | Student For Hire`}
        description={`Lawn mowing, leaf cleanups and snow removal in ${city.name} by ID-checked local students. Serving ${listNames(city.neighbourhoods.slice(0, 3))}. Free quotes.`}
        jsonLd={breadcrumbJsonLd(crumbs)}
      />
      <LocationHeader quoteTo="/book" />

      <main>
        <LocationHero
          image={HUB_HERO}
          crumbs={crumbs}
          title={`Yard work and snow removal in ${city.name}.`}
          intro={city.intro}
          quoteTo="/book"
        />
        <TrustBar />

        <section id="services" className="section">
          <div className="wrap">
            <h2 className="h2">What students take care of in {city.name}.</h2>
            <p className="lede">Every job is quoted individually. Pick a service to see how it works in {city.name}, or book a quote for anything on the list.</p>
            <ul className="local-services">
              {services.map(({ key, urlSlug, label, Icon, page }) => (
                <li key={key}>
                  <Link to={page || bookPath(urlSlug)} className="local-service">
                    <Icon size={34} />
                    <span className="local-service__text">
                      <span className="local-service__label">{page ? `${label} in ${city.name}` : label}</span>
                      <span className="local-service__blurb">{SERVICE_INFO[urlSlug].blurb}</span>
                    </span>
                    <span className="local-service__go">{page ? 'Details' : 'Get a quote'} <ArrowIcon size={15} /></span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="section section--white">
          <div className="wrap local-notes">
            <div>
              <h2 className="h2">Neighbourhoods we cover in {city.name}.</h2>
              <p className="local-notes__copy">
                We match each job with a student who lives nearby. Don't see your neighbourhood? Book a quote anyway and tell us
                where you are.
              </p>
            </div>
            <Neighbourhoods city={city} />
          </div>
        </section>

        <HowItWorks id="how" />
        <BeforeAfterGallery title={`Jobs like yours in ${city.name}.`} />
        <NearbyCities title={`Also serving near ${city.name}.`} current={city} cities={nearby} />
        <ReviewPrompt white />
        <QuoteBand title={`Book a free quote in ${city.name}.`} quoteTo="/book" />
      </main>

      <SiteFooter crossLink={{ to: '/students', label: 'For students' }} />
    </div>
  );
}

export function CityServicePage() {
  const { city: citySlug, service: urlSlug } = useParams();
  const city = findCity(citySlug);
  const page = city?.pages[urlSlug];
  const service = serviceByUrlSlug(urlSlug);
  if (!city || !page || !service) return <Navigate to="/error" replace />;

  const info = SERVICE_INFO[urlSlug];
  const heading = `${service.name} in ${city.name}`;
  const lower = service.name.toLowerCase();
  const path = cityServicePath(city, urlSlug);
  const quoteTo = bookPath(urlSlug);
  const crumbs = [
    { to: '/home-owners', label: 'Home owners' },
    { to: cityPath(city), label: city.name },
    { to: path, label: service.name },
  ];
  const nearbyWithService = city.nearby.map(findCity).filter((c) => c.pages[urlSlug]);
  const otherServices = cityServices(city).filter((s) => s.page && s.urlSlug !== urlSlug);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: heading,
      serviceType: service.name,
      description: page.description,
      url: `${SITE_URL}${path}`,
      areaServed: placeJsonLd(city),
      provider: providerJsonLd(),
    },
    faqJsonLd(page.faq),
    breadcrumbJsonLd(crumbs),
  ];

  return (
    <div className="page">
      <Seo title={`${heading} | Student For Hire`} description={page.description} image={`${SITE_URL}${info.hero.src}`} jsonLd={jsonLd} />
      <LocationHeader quoteTo={quoteTo} />

      <main>
        <LocationHero image={info.hero} crumbs={crumbs} title={heading} intro={page.intro} quoteTo={quoteTo} />
        <TrustBar />

        <section className="section">
          <div className="wrap local-notes">
            <div>
              <h2 className="h2">When to book {lower} in {city.name}.</h2>
              <p className="local-notes__copy">{page.seasonal}</p>
            </div>
            <div>
              <h3 className="h3">Neighbourhoods we cover</h3>
              <Neighbourhoods city={city} />
            </div>
          </div>
        </section>

        <BeforeAfterGallery lead={info.leadPhoto} />
        <HowItWorks id="how" />

        <Faq title={`${heading}: common questions.`} items={page.faq} white />

        <section className="section">
          <div className="wrap related">
            {nearbyWithService.length > 0 && (
              <div>
                <h2 className="h3">{service.name} near {city.name}</h2>
                <ul className="related__list">
                  {nearbyWithService.map((c) => (
                    <li key={c.slug}><Link to={cityServicePath(c, urlSlug)}>{service.name} in {c.name}</Link></li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <h2 className="h3">More in {city.name}</h2>
              <ul className="related__list">
                {otherServices.map((s) => (
                  <li key={s.key}><Link to={s.page}>{s.name} in {city.name}</Link></li>
                ))}
                <li><Link to={cityPath(city)}>All services in {city.name}</Link></li>
              </ul>
            </div>
          </div>
        </section>

        <ReviewPrompt white />
        <QuoteBand title={`Get a free ${lower} quote.`} quoteTo={quoteTo} />
      </main>

      <SiteFooter crossLink={{ to: '/students', label: 'For students' }} />
    </div>
  );
}

// Nearby cities first, then the rest of the GTA.
function NearbyCities({ title, current, cities }) {
  const others = CITIES.filter((c) => c !== current && !cities.includes(c));
  return (
    <section className="section">
      <div className="wrap">
        <h2 className="h2">{title}</h2>
        <ul className="area-grid">
          {[...cities, ...others].map((c) => (
            <li key={c.slug}><Link to={cityPath(c)} className="area-link">{c.name}</Link></li>
          ))}
        </ul>
      </div>
    </section>
  );
}
